import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Save, Move, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Campos automáticos (preenchidos na altura da assinatura)
const FIELD_DEFS = [
  { type: 'nome', label: 'Nome Completo', color: '#2563eb', defaults: { x: 0.10, y: 0.27, w: 0.45, h: 0.02 } },
  { type: 'contacto', label: 'Contacto', color: '#0d9488', defaults: { x: 0.30, y: 0.31, w: 0.30, h: 0.02 } },
  { type: 'localidade', label: 'Localidade', color: '#db2777', defaults: { x: 0.10, y: 0.86, w: 0.30, h: 0.02 } },
  { type: 'dia', label: 'Dia', color: '#ea580c', defaults: { x: 0.45, y: 0.86, w: 0.06, h: 0.02 } },
  { type: 'mes', label: 'Mês (por extenso)', color: '#d97706', defaults: { x: 0.58, y: 0.86, w: 0.16, h: 0.02 } },
  { type: 'ano', label: 'Ano', color: '#ca8a04', defaults: { x: 0.82, y: 0.86, w: 0.08, h: 0.02 } },
  { type: 'data', label: 'Data (dd/mm/aaaa)', color: '#9333ea', defaults: { x: 0.62, y: 0.86, w: 0.22, h: 0.02 } },
  { type: 'assinatura', label: 'Assinatura', color: '#4f46e5', defaults: { x: 0.58, y: 0.90, w: 0.32, h: 0.05 } },
  { type: 'assinatura_sepa', label: 'Assinatura SEPA (só c/ IBAN)', color: '#0891b2', group: 'sepa', defaults: { x: 0.10, y: 0.90, w: 0.32, h: 0.05 } },
];
const BUILTIN_TYPES = FIELD_DEFS.map((d) => d.type);
const DEFAULT_ENABLED = ['nome', 'contacto', 'localidade', 'dia', 'mes', 'ano', 'assinatura'];
const CUSTOM_COLORS = ['#e11d48', '#059669', '#7c3aed', '#0284c7', '#c026d3', '#65a30d', '#d97706', '#dc2626'];

const slugify = (s) => (s || '')
  .toString().normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export const SignatureFieldEditor = ({ documentId, documentName, open, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [acroSigPages, setAcroSigPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [imgUrl, setImgUrl] = useState(null);
  const [fieldsMap, setFieldsMap] = useState({}); // fid -> {page,x,y,w,h}
  const [enabled, setEnabled] = useState({}); // builtin type -> bool
  const [customs, setCustoms] = useState([]); // [{fid,key,label,group,color}]
  const [newLabel, setNewLabel] = useState('');
  const [newSepa, setNewSepa] = useState(false);
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  const colorFor = useCallback((fid) => {
    const b = FIELD_DEFS.find((d) => d.type === fid);
    if (b) return b.color;
    const c = customs.find((x) => x.fid === fid);
    return c ? c.color : '#334155';
  }, [customs]);

  const labelFor = useCallback((fid) => {
    const b = FIELD_DEFS.find((d) => d.type === fid);
    if (b) return b.label;
    const c = customs.find((x) => x.fid === fid);
    return c ? c.label : fid;
  }, [customs]);

  const loadPageImage = useCallback(async (page) => {
    try {
      const res = await axios.get(`${API_URL}/knowledge/${documentId}/page-image?page=${page}`, { responseType: 'blob' });
      setImgUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch {
      toast.error('Falha ao carregar a página do PDF');
    }
  }, [documentId]);

  useEffect(() => {
    if (!open || !documentId) return;
    setLoading(true);
    (async () => {
      try {
        const info = await axios.get(`${API_URL}/knowledge/${documentId}/pdf-info`);
        setPageCount(info.data.page_count || 1);
        setAcroSigPages(info.data.acro_sig_pages || []);
        const isAcro = !!info.data.acro_form;
        const map = {};
        const en = {};
        const cust = [];
        const saved = info.data.signature_fields || [];
        const hasConfig = saved.length > 0;
        let ci = 0;
        saved.forEach((f) => {
          const pos = { page: f.page || 0, x: f.x, y: f.y, w: f.w, h: f.h };
          if (f.type === 'custom') {
            const fid = 'c_' + (f.key || slugify(f.label));
            map[fid] = pos;
            cust.push({ fid, key: f.key || slugify(f.label), label: f.label || f.key, group: f.group || null, color: CUSTOM_COLORS[ci % CUSTOM_COLORS.length] });
            ci += 1;
          } else if (BUILTIN_TYPES.includes(f.type)) {
            map[f.type] = pos;
            en[f.type] = true;
          }
        });
        FIELD_DEFS.forEach((d) => {
          if (!map[d.type]) map[d.type] = { page: 0, ...d.defaults };
          if (!(d.type in en)) en[d.type] = (hasConfig || isAcro) ? false : DEFAULT_ENABLED.includes(d.type);
        });
        setFieldsMap(map);
        setEnabled(en);
        setCustoms(cust);
        setCurrentPage(0);
        await loadPageImage(0);
      } catch {
        toast.error('Falha ao abrir o documento');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentId]);

  useEffect(() => () => { if (imgUrl) URL.revokeObjectURL(imgUrl); }, [imgUrl]);

  const changePage = async (p) => { setCurrentPage(p); await loadPageImage(p); };

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    setFieldsMap((prev) => {
      const f = { ...prev[d.fid] };
      if (d.mode === 'move') {
        f.x = Math.max(0, Math.min(1 - f.w, px - d.offX));
        f.y = Math.max(0, Math.min(1 - f.h, py - d.offY));
      } else {
        f.w = Math.max(0.04, Math.min(1 - f.x, px - f.x));
        f.h = Math.max(0.015, Math.min(1 - f.y, py - f.y));
      }
      return { ...prev, [d.fid]: f };
    });
  }, []);

  const endDrag = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
  }, [onPointerMove]);

  const startMove = (e, fid) => {
    e.preventDefault(); e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    const f = fieldsMap[fid];
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    dragRef.current = { fid, mode: 'move', offX: px - f.x, offY: py - f.y };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  };
  const startResize = (e, fid) => {
    e.preventDefault(); e.stopPropagation();
    dragRef.current = { fid, mode: 'resize' };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  };

  const addCustom = () => {
    const label = newLabel.trim();
    if (!label) { toast.error('Escreva o nome do campo (ex.: CPE)'); return; }
    const key = slugify(label);
    if (!key) { toast.error('Nome de campo inválido'); return; }
    const fid = 'c_' + key;
    if (customs.some((c) => c.fid === fid)) { toast.error('Já existe um campo com esse nome'); return; }
    const color = CUSTOM_COLORS[customs.length % CUSTOM_COLORS.length];
    setCustoms((p) => [...p, { fid, key, label, group: newSepa ? 'sepa' : null, color }]);
    setFieldsMap((p) => ({ ...p, [fid]: { page: currentPage, x: 0.15, y: 0.4, w: 0.3, h: 0.025 } }));
    setNewLabel('');
    setNewSepa(false);
  };

  const removeCustom = (fid) => {
    setCustoms((p) => p.filter((c) => c.fid !== fid));
    setFieldsMap((p) => { const n = { ...p }; delete n[fid]; return n; });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const arr = [];
      FIELD_DEFS.forEach((d) => {
        if (enabled[d.type]) {
          const f = fieldsMap[d.type];
          arr.push({ type: d.type, group: d.group || undefined, page: f.page || 0, x: f.x, y: f.y, w: f.w, h: f.h });
        }
      });
      customs.forEach((c) => {
        const f = fieldsMap[c.fid];
        if (!f) return;
        arr.push({ type: 'custom', key: c.key, label: c.label, group: c.group || undefined, page: f.page || 0, x: f.x, y: f.y, w: f.w, h: f.h });
      });
      await axios.put(`${API_URL}/knowledge/${documentId}/signature-config`, { signable: true, signature_fields: arr });
      toast.success('Modelo configurado como assinável');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao guardar configuração');
    } finally {
      setSaving(false);
    }
  };

  const toggleField = (type) => {
    if (!enabled[type] && fieldsMap[type]) moveToPage(type);
    setEnabled((p) => ({ ...p, [type]: !p[type] }));
  };
  const moveToPage = (fid) => setFieldsMap((p) => ({ ...p, [fid]: { ...p[fid], page: currentPage } }));
  const pageOf = (fid) => (fieldsMap[fid]?.page || 0);
  const MoveHere = ({ fid }) => (enabled[fid] !== false && fieldsMap[fid] && pageOf(fid) !== currentPage ? (
    <button type="button" onClick={() => moveToPage(fid)} className="text-[10px] underline ml-1" data-testid={`sig-move-here-${fid}`}>
      pág. {pageOf(fid) + 1} → mover para pág. {currentPage + 1}
    </button>
  ) : null);

  // fids visíveis na página atual
  const visibleFids = [
    ...FIELD_DEFS.filter((d) => enabled[d.type] && (fieldsMap[d.type]?.page || 0) === currentPage).map((d) => d.type),
    ...customs.filter((c) => (fieldsMap[c.fid]?.page || 0) === currentPage).map((c) => c.fid),
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="signature-field-editor">
        <DialogHeader>
          <DialogTitle>Configurar Campos — {documentName}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">Ligue os campos automáticos e/ou crie campos do contrato (CPE, CUI, IBAN…) que o comercial preenche antes de gerar o link. Arraste cada etiqueta para a posição certa; o canto inferior direito redimensiona.</p>

        {acroSigPages.length > 0 && (
          <div className={`rounded-lg border p-3 text-xs ${enabled.assinatura ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`} data-testid="acro-sig-notice">
            Este PDF já tem um campo de <b>Assinatura</b> próprio (pág. {acroSigPages.join(', ')}). A assinatura do cliente vai para lá automaticamente, por isso <b>não precisa de ligar "Assinatura"</b> aqui.
            {enabled.assinatura && ' Atenção: com "Assinatura" ligado, a assinatura vai para a posição que configurar aqui e não para o campo do PDF.'}
          </div>
        )}
        <div className="flex flex-wrap gap-2 my-2">
          {FIELD_DEFS.map((d) => (
            <span key={d.type} className="inline-flex items-center" style={{ color: d.color }}>
            <button
              type="button"
              onClick={() => toggleField(d.type)}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-all ${enabled[d.type] ? '' : 'opacity-40 grayscale'}`}
              style={{ backgroundColor: d.color + '22', color: d.color, borderColor: d.color + '55' }}
              data-testid={`sig-toggle-${d.type}`}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
              {d.label}{enabled[d.type] ? ` · pág. ${pageOf(d.type) + 1}` : ' (off)'}
            </button>
            {enabled[d.type] && <MoveHere fid={d.type} />}
            </span>
          ))}
        </div>

        {/* Campos personalizados do contrato */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
              placeholder="Novo campo do contrato (ex.: CPE, CUI, IBAN, Morada)"
              className="h-9 flex-1 min-w-[220px]"
              data-testid="sig-custom-label-input"
            />
            <label className="flex items-center gap-1.5 text-xs text-cyan-700 cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-cyan-600" checked={newSepa} onChange={(e) => setNewSepa(e.target.checked)} data-testid="sig-custom-sepa" />
              Campo SEPA (só c/ IBAN)
            </label>
            <Button type="button" size="sm" className="h-9 gap-1 bg-[#2D3E6F] hover:bg-[#1a2a4a]" onClick={addCustom} data-testid="sig-add-custom-btn">
              <Plus className="w-4 h-4" /> Adicionar
            </Button>
          </div>
          {customs.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {customs.map((c) => (
                <span key={c.fid} className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border"
                  style={{ backgroundColor: c.color + '22', color: c.color, borderColor: c.color + '55' }}
                  data-testid={`sig-custom-chip-${c.key}`}>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                  {c.label}{c.group === 'sepa' ? ' · SEPA' : ''} · pág. {pageOf(c.fid) + 1}
                  <MoveHere fid={c.fid} />
                  <button type="button" onClick={() => removeCustom(c.fid)} className="ml-0.5 hover:opacity-70" data-testid={`sig-custom-remove-${c.key}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {pageCount > 1 && (
          <div className="flex items-center gap-2 my-2">
            <span className="text-xs text-slate-500">Página:</span>
            {Array.from({ length: pageCount }).map((_, i) => (
              <Button key={i} size="sm" variant="outline" className={`h-7 px-2 ${currentPage === i ? 'bg-[#2D3E6F] text-white border-[#2D3E6F] hover:bg-[#1a2a4a] hover:text-white' : ''}`} onClick={() => changePage(i)} data-testid={`sig-page-btn-${i + 1}`}>{i + 1}</Button>
            ))}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-96"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div ref={containerRef} className="relative w-full select-none border border-slate-200 rounded-lg overflow-hidden bg-slate-50 mt-2" style={{ touchAction: 'none' }}>
            {imgUrl && <img src={imgUrl} alt="Página" className="w-full block pointer-events-none" draggable={false} />}
            {visibleFids.map((fid) => {
              const f = fieldsMap[fid];
              if (!f) return null;
              const color = colorFor(fid);
              return (
                <div
                  key={fid}
                  onPointerDown={(e) => startMove(e, fid)}
                  className="absolute flex items-center justify-center text-[10px] font-semibold rounded cursor-move"
                  style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`, backgroundColor: color + '33', border: `2px solid ${color}`, color }}
                  data-testid={`sig-field-${fid}`}
                >
                  <Move className="w-3 h-3 mr-1" />{labelFor(fid)}
                  <span onPointerDown={(e) => startResize(e, fid)} className="absolute -bottom-1 -right-1 w-3 h-3 rounded-sm cursor-se-resize" style={{ backgroundColor: color }} />
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || loading} className="bg-[#2D3E6F] hover:bg-[#1a2a4a]" data-testid="save-signature-config-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar Configuração
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
